import { isAfter, isBefore } from 'date-fns';
import type { Popup } from '../types/popup.types';

const POPUP_STORAGE_KEY = 'popup_history';

/**
 * Popup'ın gösterilip gösterilmeyeceğini kontrol eder
 */
export const shouldShowPopup = (
  popup: Popup,
  currentPath: string,
  currentVendorId: number
): boolean => {
  // 1. Aktif mi?
  if (!popup.isActive) {
    return false;
  }

  // 2. Tarih aralığı kontrolü
  const now = new Date();
  if (popup.startAt && isBefore(now, new Date(popup.startAt))) {
    return false;
  }
  if (popup.endAt && isAfter(now, new Date(popup.endAt))) {
    return false;
  }

  // 3. Sayfa hedefleme kontrolü
  if (popup.targetPage && popup.targetPage.length > 0) {
    const matchesPage = popup.targetPage.some(pageObj => {
      if (!pageObj.isActive) return false;
      if (pageObj.slug === currentPath) return true;
      return currentPath.startsWith(pageObj.slug);
    });

    if (!matchesPage) {
      return false;
    }
  }

  // 4. Vendor filtering (includedVendors)
  if (popup.includedVendors && popup.includedVendors.length > 0) {
    if (!popup.includedVendors.includes(currentVendorId)) {
      return false;
    }
  }

  // 5. Vendor filtering (excludedVendors)
  if (popup.excludedVendors && popup.excludedVendors.length > 0) {
    if (popup.excludedVendors.includes(currentVendorId)) {
      return false;
    }
  }

  // 6. Frequency kontrolü
  if (!checkFrequency(popup)) {
    return false;
  }

  return true;
};

/**
 * Popup gösterim sıklığını kontrol eder
 */
const checkFrequency = (popup: Popup): boolean => {
  if (!popup.popupFrequency) return true;

  const frequencyName = popup.popupFrequency.name;

  switch (frequencyName) {
    case 'every_visit':
      return true;

    case 'once_per_session':
      try {
        const sessionKey = `popup_session_${popup.id}`;
        const sessionShown = sessionStorage.getItem(sessionKey);
        return !sessionShown;
      } catch {
        return true;
      }

    case 'once_per_day':
      const history = getPopupHistory();
      const lastShown = history[popup.id];
      if (!lastShown) return true;
      
      const now = Date.now();
      const timeDiff = now - lastShown;
      return timeDiff > 24 * 60 * 60 * 1000;

    default:
      return true;
  }
};

/**
 * Popup geçmişini localStorage'dan al
 */
const getPopupHistory = (): Record<number, number> => {
  try {
    const history = localStorage.getItem(POPUP_STORAGE_KEY);
    return history ? JSON.parse(history) : {};
  } catch {
    return {};
  }
};

/**
 * Popup kapatıldığında işaretle
 */
export const markPopupAsShown = (popup: Popup): void => {
  try {
    const frequencyName = popup.popupFrequency?.name;

    switch (frequencyName) {
      case 'every_visit':
        break;

      case 'once_per_session':
        const sessionKey = `popup_session_${popup.id}`;
        sessionStorage.setItem(sessionKey, Date.now().toString());
        break;

      case 'once_per_day':
        const history = getPopupHistory();
        history[popup.id] = Date.now();
        localStorage.setItem(POPUP_STORAGE_KEY, JSON.stringify(history));
        break;

      default:
        break;
    }
  } catch {
    // Storage error - silent fail
  }
};

/**
 * Display timing'e göre gecikme süresini hesapla (ms)
 */
export const getDisplayDelay = (popup: Popup): number => {
  if (!popup.displayTiming) return 0;

  const timing = popup.displayTiming.name;

  switch (timing) {
    case 'onLoad':
      return 0;
    case 'onScroll':
      return -2;
    case 'onExit':
      return -1;
    default:
      return 0;
  }
};
